import { ArrowLeft, Share2, Send, MessageCircle, Target } from "lucide-react";
import { toast } from "sonner";
import MatchCard from "@/components/MatchCard";
import { shareMovimentiImage, buildShareText } from "@/lib/shareImage";

function Dot({ active, gold }) {
  const cls = gold
    ? "bg-[#E3BC55] shadow-[0_0_10px_rgba(227,188,85,0.5)]"
    : active
    ? "bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.4)]"
    : "bg-[#D9D9D9]";
  return <span className={`block w-9 h-9 sm:w-10 sm:h-10 rounded-full mx-auto transition-colors ${cls}`} />;
}

export default function MovimentiPage({ analysis, goToCalcolo }) {
  if (!analysis) {
    return (
      <div className="text-center py-20 fade-up">
        <p className="text-slate-500 mb-4">Nessuna analisi disponibile. Completa prima la Fase Calcolo.</p>
        <button onClick={goToCalcolo} className="text-[#E3BC55] font-heading uppercase text-sm tracking-widest hover:text-[#FFD700]">
          <ArrowLeft className="w-4 h-4 inline mr-1" /> Vai alla Fase Calcolo
        </button>
      </div>
    );
  }

  const { match, movements } = analysis;

  const best = (keys) =>
    movements.filter((m) => keys.includes(m.market)).sort((a, b) => b.prob_pct - a.prob_pct)[0];
  const bestEsito = best(["1", "2", "1X", "X2"]);
  const activeMultigol = movements
    .filter((m) => m.market.startsWith("Multigol") && m.active && m.intensity >= 1)
    .sort((a, b) => b.prob_pct - a.prob_pct)[0];
  const bestOU = activeMultigol || best(["Over 1.5", "Over 2.5", "Under 2.5", "Under 3.5"]);

  const shareImage = async () => {
    try {
      const res = await shareMovimentiImage(match, movements);
      if (res === "downloaded") toast.success("Immagine scaricata! Condividila dove vuoi");
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Condivisione non riuscita");
    }
  };

  const text = encodeURIComponent(buildShareText(match, movements));

  return (
    <div className="fade-up max-w-xl mx-auto space-y-8">
      <MatchCard match={match} />

      <h1 className="font-heading font-bold text-2xl sm:text-3xl text-[#E3BC55] uppercase tracking-[0.18em] text-center">
        Movimenti Consigliati
      </h1>

      {bestEsito && bestOU && (
        <div data-testid="movimento-consigliato-card" className="border-2 border-[#E3BC55] rounded-2xl bg-black px-6 py-5 gold-glow text-center">
          <p className="font-mono-data text-[11px] text-[#E3BC55] tracking-[0.25em] uppercase flex items-center justify-center gap-2 mb-3">
            <Target className="w-3.5 h-3.5" /> Movimento Consigliato
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <span className="bg-[#E3BC55] text-black font-heading font-bold text-xl sm:text-2xl rounded-xl px-4 py-2">
              {bestEsito.market}
            </span>
            <span className="text-[#E3BC55] font-heading font-bold text-2xl">+</span>
            <span className="bg-[#E3BC55] text-black font-heading font-bold text-xl sm:text-2xl rounded-xl px-4 py-2">
              {bestOU.market}
            </span>
          </div>
          <p className="font-mono-data text-xs text-slate-400 mt-3">
            {bestEsito.market}: <span className="text-emerald-400">{bestEsito.prob_pct}%</span>
            <span className="mx-2 text-slate-600">·</span>
            {bestOU.market}: <span className="text-emerald-400">{bestOU.prob_pct}%</span>
          </p>
        </div>
      )}

      <div data-testid="movimenti-matrix-table" className="border-2 border-[#E3BC55] rounded-2xl bg-black px-4 sm:px-6 py-5">
        <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr_1fr] items-center pb-3 border-b border-slate-700/60">
          <span className="text-[#E3BC55] font-heading font-bold text-lg uppercase tracking-wider">Esito</span>
          {["1", "2", "3", "Star"].map((h) => (
            <span key={h} className="text-[#E3BC55] font-heading font-bold text-lg text-center">{h}</span>
          ))}
        </div>

        {movements.map((m) => (
          <div
            key={m.market}
            data-testid={`movimenti-row-${m.market.replace(/[\s.]/g, "-").toLowerCase()}`}
            className="grid grid-cols-[1.4fr_1fr_1fr_1fr_1fr] items-center py-3.5"
            title={`${m.label} · Probabilità ${m.prob_pct}%${m.fair_odds ? ` · Quota fair ${m.fair_odds}` : ""}`}
          >
            <span className="text-white font-heading font-bold text-lg sm:text-xl">{m.market}</span>
            <Dot active={m.intensity >= 1} />
            <Dot active={m.intensity >= 2} />
            <Dot active={m.intensity >= 3} />
            <Dot active={m.star} gold={m.star} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-[1fr_auto_auto] gap-3">
        <button
          data-testid="share-image-button"
          onClick={shareImage}
          className="py-3.5 rounded-xl bg-[#E3BC55] text-black font-heading font-bold text-base uppercase tracking-[0.12em] hover:bg-[#FFD700] transition-colors flex items-center justify-center gap-2"
        >
          <Share2 className="w-4 h-4" /> Condividi Immagine
        </button>
        <a
          data-testid="share-whatsapp-button"
          href={`https://wa.me/?text=${text}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 rounded-xl border-2 border-[#E3BC55]/60 hover:border-[#E3BC55] text-[#E3BC55] flex items-center justify-center transition-colors"
          title="Condividi su WhatsApp"
        >
          <MessageCircle className="w-5 h-5" />
        </a>
        <a
          data-testid="share-telegram-button"
          href={`https://t.me/share/url?url=zerosignals&text=${text}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 rounded-xl border-2 border-[#E3BC55]/60 hover:border-[#E3BC55] text-[#E3BC55] flex items-center justify-center transition-colors"
          title="Condividi su Telegram"
        >
          <Send className="w-5 h-5" />
        </a>
      </div>

      <button
        onClick={goToCalcolo}
        data-testid="movimenti-nuova-analisi-button"
        className="w-full py-4 rounded-xl bg-[#E3BC55] text-black font-heading font-bold text-xl uppercase tracking-[0.15em] hover:bg-[#FFD700] transition-colors"
      >
        Nuova Analisi
      </button>
    </div>
  );
}
